import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	type OutreachExcludedPerson,
	OutreachExclusionReason,
	type OutreachFactor,
	OutreachFactorKind,
	type OutreachLevel,
	type OutreachMatch,
	type OutreachScoredPerson,
} from "../../../utils/models";
import { levelNumberOf } from "./outreachGroupTypes";

/** Factors that put someone at the end of the line whatever else helps. */
const ASK_LAST_KINDS: ReadonlySet<OutreachFactorKind> = new Set([OutreachFactorKind.AcceptedYesterday, OutreachFactorKind.NeverAccepted]);

export const DATE_FORMAT = "ccc, MMM d";
export const TIME_FORMAT = "h:mm a";
export const MONTH_DAY = "MMM d";

export const timeRangeOf = (from: DateTime, to: DateTime): string => `${from.toFormat(TIME_FORMAT)}-${to.toFormat(TIME_FORMAT)}`;
export const dateTimeOf = (at: DateTime): string => `${at.toFormat(DATE_FORMAT)} · ${at.toFormat(TIME_FORMAT)}`;

/** "Today · 10:00 AM", "Tomorrow · 10:00 AM", or the date and time. */
export const dayMetaOf = (t: TFunction, at: DateTime, now: DateTime): string => {
	if (at.hasSame(now, "day")) return t("request.outreach.when.today", { time: at.toFormat(TIME_FORMAT) });
	if (at.hasSame(now.plus({ days: 1 }), "day")) return t("request.outreach.when.tomorrow", { time: at.toFormat(TIME_FORMAT) });
	return dateTimeOf(at);
};

/** "Oct 6-9", "Oct 8", "Sep 30-Oct 2". */
const dayRangeOf = (from: DateTime, to: DateTime): string => {
	if (from.hasSame(to, "day")) return from.toFormat(MONTH_DAY);
	return from.hasSame(to, "month") ? `${from.toFormat(MONTH_DAY)}-${to.toFormat("d")}` : `${from.toFormat(MONTH_DAY)}-${to.toFormat(MONTH_DAY)}`;
};

/** What moved someone in line, in reading order: an ask-last reason first, then helps strongest first, then drags worst first. */
export const orderedFactorsOf = (person: OutreachScoredPerson): OutreachFactor[] => {
	const askLast = person.factors.filter((factor) => ASK_LAST_KINDS.has(factor.kind));
	const rest = person.factors.filter((factor) => !ASK_LAST_KINDS.has(factor.kind));
	const helps = rest.filter((factor) => factor.strength > 0).sort((a, b) => b.strength - a.strength);
	const drags = rest.filter((factor) => factor.strength < 0).sort((a, b) => a.strength - b.strength);
	return [...askLast, ...helps, ...drags];
};

/** Every outreach string built from data: factor text, reasons, labels. Strings that are plain keys stay inline at the call site. */
export const useOutreachCopy = () => {
	const { t } = useTranslation();
	return useMemo(() => {
		const factorText = (factor: OutreachFactor): string => {
			if (factor.kind === OutreachFactorKind.PrefersShiftType) {
				return t("request.outreach.factor.prefers_shift_type", { shiftType: t(`request.outreach.shift_type.${factor.shiftType}`) });
			}
			return "count" in factor ? t(`request.outreach.factor.${factor.kind}`, { count: factor.count }) : t(`request.outreach.factor.${factor.kind}`);
		};
		const matchLabel = (match: OutreachMatch): string => t(`request.outreach.match.${match}`);
		const people = (count: number): string => t("request.outreach.people", { count });
		const levelName = (level: OutreachLevel): string => t(`request.outreach.level.${level}.name`);
		/** "Level 3" */
		const levelNumber = (level: OutreachLevel): string => t("request.outreach.level.number", { number: levelNumberOf(level) });
		/** "Level 3 · Allow calls" */
		const levelOption = (level: OutreachLevel): string => t("request.outreach.level.option", { number: levelNumberOf(level), name: levelName(level) });
		const exclusionText = (person: OutreachExcludedPerson): string => {
			switch (person.reason) {
				case OutreachExclusionReason.ChargeNurse:
				case OutreachExclusionReason.ProfileInactive:
					return t(`request.outreach.exclusion.${person.reason}`);
				case OutreachExclusionReason.Role:
					return person.role;
				case OutreachExclusionReason.LastDay:
					return t("request.outreach.exclusion.last_day", { date: person.date.toFormat(DATE_FORMAT) });
				case OutreachExclusionReason.Pto:
					return t("request.outreach.exclusion.pto", { range: dayRangeOf(person.from, person.to) });
				case OutreachExclusionReason.OnLeave:
				case OutreachExclusionReason.Suspended:
					return person.to === null
						? t(`request.outreach.exclusion.${person.reason}`)
						: t(`request.outreach.exclusion.${person.reason}_until`, { date: person.to.toFormat(DATE_FORMAT) });
				case OutreachExclusionReason.WorkingShift:
				case OutreachExclusionReason.WorkingThatDay:
					return t(`request.outreach.exclusion.${person.reason}`, { range: timeRangeOf(person.from, person.to) });
			}
		};
		return { factorText, matchLabel, people, levelName, levelNumber, levelOption, exclusionText };
	}, [t]);
};

export type OutreachCopy = ReturnType<typeof useOutreachCopy>;
